import React, { createContext, lazy, Suspense, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { BrowserRouter, useLocation } from "react-router-dom";
import Router from "./pages/Router";
import { usePostHog } from '@posthog/react';
import {ThemeProvider} from "@material-ui/core/styles";
import theme from "./AppTheme";
import {appReducer, State} from "./redux/reducers";
import {SnackbarProvider} from "notistack";
import Notifier 
,{
    closeSnackbar as closeSnackbarAction,
    enqueueSnackbar as enqueueSnackbarAction
}
 from "./components/Notifier";
import {iApiBasicResponse} from "./types/api";
import BaseAPIs from "./apis/base.apis";
import CustomModal from "./components/Modal/CustomModal";
import {BreadCrumbContext} from "./components/Breadcrumbs/WithBreadcrumb";
import {chatReducer, ChatState} from "./redux/reducers/chatReducer";
import {TourProvider} from "@reactour/tour";
import { ProductTourProvider } from "./components/ProductTour";
import {TOUR_SKIP} from "./components/Tour/Pages/Home.Tour";
import {hideChat, ShowEWChat} from "./components/ewChatEmbed";
import { EwToaster, fireEwToast } from './components/ewToast';
import ErrorBoundary from './components/ErrorBoundary';
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import I18nLangSync from "./components/i18n/I18nLangSync";
import { registerEmailwishApiTokenInterceptor } from "./utils/emailwishApiToken";

export const AppStateContext = createContext<State>({
    notifications: [],
    userInteracted: false,
    server_notifications: {loading: false, resource: undefined, query: {per_page: 10, page: 1}}
});
export const AppDispatchContext = createContext<any>({});
export const ChatAppStateContext = createContext<ChatState>({});
export const ChatAppDispatchContext = createContext<any>({});

export interface NotificationDispatch {
    pushSuccessNotification: (message?: string) => void,
    pushErrorNotification: (message?: string) => void,
    pushNotification: (response: iApiBasicResponse) => void,
}

export const NotificationContext = createContext<NotificationDispatch>({
    pushSuccessNotification: message => {
    },
    pushErrorNotification: message => {
    },
    pushNotification: response => {
    },
});


const WebsocketHandler = lazy(() => import('./components/Websocket/WebsockerHandler'));
const WebsocketHandlerEcho = lazy(() => import('./components/Websocket/WebsockerHandlerEcho'));
const ChatPresenceHeartbeat = lazy(() => import('./components/chat/ChatPresenceHeartbeat'));

const POSTHOG_SESSION_LEADER_KEY = 'posthog_session_recording_leader';

function PostHogPageView() {
    const location = useLocation();
    const posthog = usePostHog();
    const posthogRef = useRef(posthog);
    const lastCapturedPathnameRef = useRef<string | null>(null);
    posthogRef.current = posthog;
    useEffect(() => {
        const pathname = location.pathname;
        if (!pathname) return;
        if (lastCapturedPathnameRef.current === pathname) return;
        const ph = posthogRef.current;
        if (!ph) return;
        lastCapturedPathnameRef.current = pathname;
        ph.capture('$pageview', { $current_url: window.location.href });
    }, [location.pathname]);
    return null;
}

function PostHogSessionRecordingLeader() {
    const posthog = usePostHog();
    const hasRunRef = useRef(false);
    useEffect(() => {
        if (hasRunRef.current) return;
        if (!posthog?.startSessionRecording && !posthog?.stopSessionRecording) return;
        hasRunRef.current = true;
        try {
            if (!sessionStorage.getItem(POSTHOG_SESSION_LEADER_KEY)) {
                sessionStorage.setItem(POSTHOG_SESSION_LEADER_KEY, '1');
                posthog.startSessionRecording?.();
            } else {
                posthog.stopSessionRecording?.();
            }
        } catch {
            // ignore
        }
    }, [posthog]);
    return null;
}

export default function App() {
    const [state, dispatch] = useReducer(appReducer, {
        notifications: [],
        userInteracted: false,
        popup_tour_shown: true,
        server_notifications: {loading: false, resource: undefined, query: {per_page: 10, page: 1}}
    },);
    const {tour} = state;
    const [chatState, chatDispatch] = useReducer(chatReducer, {});
    const [posthogMounted, setPosthogMounted] = useState(false);

    const pushToast = useCallback((type: "Success" | "Error", message: string) => {
        const heading =
            type === "Success"
                ? i18n.t("notifications.successTitle", { ns: "common" })
                : i18n.t("notifications.errorTitle", { ns: "common" });
        fireEwToast(type, message, heading);
    }, []);

    const pushSuccessNotification = useCallback((message?: string) => {
        if (message) {
            pushToast('Success', message);
        }
    }, [pushToast]);

    const pushErrorNotification = useCallback((message?: string) => {
        if (message) {
            pushToast('Error', message);
        }
    }, [pushToast]);

    const pushNotification = useCallback((response: iApiBasicResponse) => {
        if (BaseAPIs.hasErrorNotification(response)) {
            pushErrorNotification(response.message);
        } else {
            pushSuccessNotification(response.message)
        }
    }, [pushErrorNotification, pushSuccessNotification]);

    const notificationDispatch = useMemo(
        () => ({
            pushErrorNotification,
            pushSuccessNotification,
            pushNotification,
        }),
        [pushErrorNotification, pushSuccessNotification, pushNotification]
    );
    const [links, setLinks] = useState<Array<{
        text: string,
        link: string
    }>>([]);
    const onBreadCrumbChange = useCallback((links_temp: any) => {
        setLinks(links_temp);
    }, []);

    const [headingTitle, setHeadingTitle] = useState<string | null>(null);
    const onHeadingTitleChange = useCallback((title: string | null) => {
        setHeadingTitle(title);
    }, []);

    function closeTour() {
        if (tour) {
            if (tour.tour_type !== TOUR_SKIP) {
                dispatch({type: "set_tour", tour: {...tour, tour_type: TOUR_SKIP}})
            } else {
                dispatch({type: "set_tour", tour: {...tour, tour_active: false}})
            }
        }
    }

    useEffect(() => {
        registerEmailwishApiTokenInterceptor();
    }, []);

    const loggedIn = Boolean(state.loggedInUser);

    useEffect(() => {
        if (!loggedIn) {
            hideChat();
            return;
        }
        // Defer storefront-widget boot — it storms APIs and blocks load on guest pages.
        const idle = window.setTimeout(() => ShowEWChat(), 1500);
        return () => {
            window.clearTimeout(idle);
            hideChat();
        };
    }, [loggedIn]);

    useEffect(() => {
        setPosthogMounted(true);
    }, []);

    const isPostHogLeader = typeof window !== 'undefined' && !!(window as any).__POSTHOG_LEADER__;

    return <ThemeProvider theme={theme}>
        <I18nextProvider i18n={i18n}>
            <I18nLangSync />
        <BrowserRouter>
            {isPostHogLeader && posthogMounted && (
                <>
                    <PostHogPageView />
                    <PostHogSessionRecordingLeader />
                </>
            )}
            <AppDispatchContext.Provider value={dispatch}>
                <AppStateContext.Provider value={state}>
                    <TourProvider
                        showCloseButton={false}
                        steps={[]}
                        maskClassName="tour-mask"
                        inViewThreshold={5}
                        showBagde={false}
                        showNavigation={false}
                        className="helper"
                        beforeClose={closeTour}>
                        <ChatAppDispatchContext.Provider value={chatDispatch}>
                            <ChatAppStateContext.Provider value={chatState}>
                                {loggedIn && (
                                    <Suspense fallback={null}>
                                        <WebsocketHandler/>
                                        <ChatPresenceHeartbeat/>
                                        <ErrorBoundary>
                                            <WebsocketHandlerEcho/>
                                        </ErrorBoundary>
                                    </Suspense>
                                )}
                                <EwToaster />

                                <SnackbarProvider>
                                    <NotificationContext.Provider value={notificationDispatch}>
                                        <BreadCrumbContext.Provider value={useMemo(() => ({
                                            links: links,
                                            setLinks: onBreadCrumbChange,
                                            headingTitle,
                                            setHeadingTitle: onHeadingTitleChange,
                                        }), [links, onBreadCrumbChange, headingTitle, onHeadingTitleChange])}>

                                            <CustomModal>
                                                <ProductTourProvider>
                                                    <Router/>
                                                </ProductTourProvider>
                                            </CustomModal>
                                        </BreadCrumbContext.Provider>
                                    </NotificationContext.Provider>
                                    <Notifier/>
                                </SnackbarProvider>
                            </ChatAppStateContext.Provider>
                        </ChatAppDispatchContext.Provider>
                    </TourProvider>
                </AppStateContext.Provider>
            </AppDispatchContext.Provider>
        </BrowserRouter>
        </I18nextProvider>
    </ThemeProvider>
};
